import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Target } from "lucide-react";
import productsData from "../data/products.json";

const allProducts = Object.fromEntries(
  Object.entries(productsData).map(([key, value]) => [
    key,
    { title: value.title, items: value.items.map((item) => item.name) },
  ])
);

const AllProducts = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      const timer = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <section className="relative min-h-screen py-24 md:py-32 bg-gray-50">
      <div className="container relative z-10">
        <Link to="/#products">
          <motion.button
            className="flex items-center gap-2 text-brand-navy hover:text-brand-yellow mb-12 transition-colors font-medium"
            whileHover={{ x: -5 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Products
          </motion.button>
        </Link>

        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-navy mb-4">
            All Products
            <span className="block text-brand-yellow mt-2">
              Complete Catalog
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Premium agricultural exports from Ethiopia
          </p>
        </motion.div>

        <div className="space-y-16">
          {Object.entries(allProducts).map(([category, data], catIndex) => (
            <motion.div
              key={category}
              id={category}
              className="scroll-mt-24"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: catIndex * 0.2 }}
            >
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                {data.title}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {data.items.map((item, index) => {
                  const cardIndex = catIndex * 10 + index;
                  const isHovered = hoveredCard === cardIndex;
                  return (
                    <motion.div
                      key={index}
                      className="group relative"
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      onHoverStart={() => setHoveredCard(cardIndex)}
                      onHoverEnd={() => setHoveredCard(null)}
                    >
                      <motion.div
                        className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                        whileHover={{ y: -8 }}
                      >
                        <img
                          src={`/${
                            item === "Turmeric" ? "tumeric" : item.toLowerCase()
                          }.jpg`}
                          alt={item}
                          loading="lazy"
                          className="w-full h-96 object-cover"
                        />

                        <div className="absolute bottom-0 left-0 right-0 backdrop-blur-md bg-black/30 p-4">
                          <h3 className="text-lg font-bold text-white text-center">
                            {item}
                          </h3>
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AllProducts;
